import { Product } from '@@types'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useIntersectionObserver } from 'usehooks-ts'

import { ProductCard } from '@widgets/product-preview'

import { useAnalytics } from '@shared/lib/analytics'
import ArrowLink from '@shared/ui/arrow-link'
import CardSkeleton from '@shared/ui/card-skeleton'

import styles from './styles.module.scss'


interface ProductCarouselProps {
  title?: string;
  products: Product[];
  link?: string;
  loading?: boolean;
  swiperClassName?: string;
  onLinkClick?: () => void;
  /** Название списка для аналитики */
  listName?: string;
  /** ID списка для аналитики */
  listId?: string;
}

function ProductsLoading () {
  return (
    <div className={styles.skeleton}>
      {Array.from({ length: 8 }).map((_, index) => (
        <CardSkeleton key={index} />
      ))}
    </div>
  )
}

export function ProductCarousel (props: ProductCarouselProps) {
  const { title, products, link, loading, swiperClassName, onLinkClick, listName, listId } = props
  const { t: tc } = useTranslation('common')
  const { viewItemList } = useAnalytics()

  // Определяем название списка для аналитики
  const analyticsListName = listName || title || 'Product Carousel'
  const analyticsListId = listId || 'product_carousel'

  // Стабильный ключ на основе ID продуктов для избежания повторных вызовов
  const productsKey = useMemo(
    () => products?.map((p) => p.id).join(',') ?? '',
    [ products ]
  )
  
  // Отслеживаем, был ли уже отправлен impression для этого набора продуктов
  const trackedKeyRef = useRef<string>('')

  // Intersection Observer для отслеживания появления в области видимости
  const { ref: observerRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.5, // 50% элемента должно быть видно
    freezeOnceVisible: true, // Остановить наблюдение после первого появления
  })

  // Track view_item_list только когда карусель появляется в области видимости
  useEffect(() => {
    if (
      isIntersecting &&
      products?.length &&
      !loading &&
      productsKey &&
      productsKey !== trackedKeyRef.current
    ) {
      trackedKeyRef.current = productsKey
      viewItemList(products, analyticsListName, analyticsListId)
    }
  }, [ isIntersecting, productsKey, loading, viewItemList, analyticsListName, analyticsListId, products ])

  if (loading) {
    return <ProductsLoading />
  }

  if (!products?.length) {
    return null
  }

  return (
    <section ref={observerRef} className={styles.root}>
      {(title || link) && (
        <div className={styles.root__top}>
          {title && <h2 className={styles.root__title}>{title}</h2>}
          {link && <ArrowLink to={link} text={tc('more')} onClick={onLinkClick} />}
        </div>
      )}

      <Swiper
        spaceBetween={8}
        slidesPerGroup={2}
        slidesPerView="auto"
        modules={[ Navigation ]}
        navigation={true}
        className={swiperClassName}
      >
        {products.map((p) => {
          return (
            <SwiperSlide className={styles.slide} key={p.id}>
              <ProductCard data={p} listName={analyticsListName} listId={analyticsListId} />
            </SwiperSlide>
          )
        })}
      </Swiper>
    </section>
  )
}
